import {
  ApInput,
  ApSelect,
  ApTooltip,
  ApIcon,
  ApButtonSecondary,
} from '@alixpartners/ui-components';
import * as React from 'react';
import { IndustryRow } from '../../../components/IndustryRow/IndustryRow';
import {
  ErrorRow,
  ErrorText,
  IndustryNameWrapper,
  InputLabel,
  LabelRow,
  SelectContainer,
  SelectYearDescription,
  TooltipContent,
  IndustryLabel,
  AllocationLabel,
  ConfigurationRow,
} from './Benchmarks.styles';
import {
  getFilteredOptions,
  getYearInputHint,
  REVENUE_UPLIFT_INPUT_HINT,
} from './Benchmarks.helpers';
import {
  IProviderIndustry,
  IApIndustry,
  INDUSTRY_PROVIDERS,
  IIndustryAllocationLocal,
  IApIndustryOption,
  IProviderIndustryOption,
} from 'utils/types';

interface IndustryAllocationRow extends IIndustryAllocationLocal {
  isMissingData: any;
}

interface INewIndustryBlendProps {
  addIndustry: () => void;
  apIndustries: IApIndustry[];
  availableYears: number[];
  industries: IndustryAllocationRow[];
  industryBlendName: string;
  isExistingBlendName: boolean;
  missingDataProviders: any[];
  missingProviders: any[];
  providerIndustries: IProviderIndustry[];
  removeIndustry: (id: number) => void;
  resetIndustry: (id: number) => void;
  revenueUpliftPercentage: string;
  setRevenueUpliftPercentage: (o: string) => void;
  setYear: (o: string) => void;
  setIndustryBlendName: (o: string) => void;
  updateIndustries: (id: number, industry: IIndustryAllocationLocal) => void;
  year: string;
}

const getApIndustriesOptions = (
  apIndustries: IApIndustry[]
): IApIndustryOption[] =>
  apIndustries.map((industry) => ({
    ...industry,
    value: industry.l1Code,
    name: industry.l1Category,
  }));

const getProviderIndustriesOptions = (
  providerIndustries: IProviderIndustry[]
): IProviderIndustryOption[] =>
  providerIndustries.map((industry) => ({
    value: industry.l1Code,
    name: industry.l1Category,
    ...industry,
  }));

export const IndustryBlendForm = ({
  addIndustry,
  apIndustries,
  availableYears,
  industries,
  industryBlendName,
  isExistingBlendName,
  missingDataProviders,
  missingProviders,
  providerIndustries,
  removeIndustry,
  resetIndustry,
  revenueUpliftPercentage,
  setIndustryBlendName,
  setRevenueUpliftPercentage,
  setYear,
  updateIndustries,
  year,
}: INewIndustryBlendProps) => {
  const computerEconomicsIndustries = providerIndustries.filter(
    (industry) => industry.provider === INDUSTRY_PROVIDERS.COMPUTER_ECONOMICS
  );
  const gartnerIndustries = providerIndustries.filter(
    (industry) => industry.provider === INDUSTRY_PROVIDERS.GARTNER
  );
  const yearOptions = availableYears.map((year) => ({
    label: String(year),
    value: String(year),
  }));
  const totalAllocation = industries.reduce((acc, industry) => {
    return acc + industry.allocationPercentage;
  }, 0);
  const isCorrectAllocation = totalAllocation === 100;
  const apIndustriesOptionsAll = getApIndustriesOptions(apIndustries);
  const computerEconomicsIndustriesOptionsAll = getProviderIndustriesOptions(
    computerEconomicsIndustries
  );
  const gartnerIndustriesOptionsAll =
    getProviderIndustriesOptions(gartnerIndustries);
  return (
    <>
      <ConfigurationRow>
        <IndustryNameWrapper>
          <InputLabel>Name of industry blend</InputLabel>
          <ApInput
            value={industryBlendName}
            onChange={setIndustryBlendName}
            errorMessage={
              isExistingBlendName ? 'Name already exist' : undefined
            }
          />
        </IndustryNameWrapper>
        <SelectContainer>
          <SelectYearDescription>
            <InputLabel>Select benchmark year</InputLabel>
            <ApSelect
              name="select-benchmark-year"
              options={yearOptions}
              onChange={setYear}
              value={String(year)}
            />
          </SelectYearDescription>

          <ApTooltip
            enterDelay={0}
            placement="right"
            message={getYearInputHint(Math.max.apply(null, availableYears))}
          >
            <TooltipContent>
              <ApIcon iconName="help_outline" />
            </TooltipContent>
          </ApTooltip>
        </SelectContainer>
        <SelectContainer>
          <SelectYearDescription>
            <InputLabel>Benchmark metric uplift</InputLabel>
            <ApInput
              name="benchmark-metric-uplift"
              placeholder="(optional)"
              value={revenueUpliftPercentage}
              errorMessage={
                revenueUpliftPercentage !== '' &&
                (Number(revenueUpliftPercentage) < 1 ||
                  Number(revenueUpliftPercentage) > 25)
                  ? 'uplift must be in range 1-25'
                  : undefined
              }
              onChange={setRevenueUpliftPercentage}
              symbol="%"
            />
          </SelectYearDescription>

          <ApTooltip
            enterDelay={0}
            placement="right"
            message={REVENUE_UPLIFT_INPUT_HINT}
          >
            <TooltipContent>
              <ApIcon iconName="help_outline" />
            </TooltipContent>
          </ApTooltip>
        </SelectContainer>
      </ConfigurationRow>
      <LabelRow>
        <IndustryLabel>AlixPartners Industry</IndustryLabel>
        <AllocationLabel>Allocation</AllocationLabel>
        <IndustryLabel>Equivalent Gartner Industry</IndustryLabel>
        <IndustryLabel>Equivalent Computer Economics Industry</IndustryLabel>
      </LabelRow>
      {industries.map((industry, id) => {
        const {
          apIndustryCode,
          computerEconomicsIndustryCode,
          gartnerIndustryCode,
        } = industry;
        const {
          apIndustriesOptions,
          computerEconomicsIndustriesOptions,
          gartnerIndustriesOptions,
        } = getFilteredOptions({
          apIndustry: apIndustryCode,
          gartnerIndustry: gartnerIndustryCode,
          computerEconomicsIndustry: computerEconomicsIndustryCode,
          apIndustriesOptions: apIndustriesOptionsAll,
          computerEconomicsIndustriesOptions:
            computerEconomicsIndustriesOptionsAll,
          gartnerIndustriesOptions: gartnerIndustriesOptionsAll,
        });
        if (apIndustriesOptions.length === 1 && !apIndustryCode) {
          updateIndustries(id, {
            ...industry,
            apIndustryCode: apIndustriesOptions[0].value,
          });
        }
        if (gartnerIndustriesOptions.length === 1 && !gartnerIndustryCode) {
          updateIndustries(id, {
            ...industry,
            gartnerIndustryCode: gartnerIndustriesOptions[0].value,
          });
        }
        if (
          computerEconomicsIndustriesOptions.length === 1 &&
          !computerEconomicsIndustryCode
        ) {
          updateIndustries(id, {
            ...industry,
            computerEconomicsIndustryCode:
              computerEconomicsIndustriesOptions[0].value,
          });
        }
        return (
          <ConfigurationRow
            key={`${id}-${apIndustryCode}-${computerEconomicsIndustryCode}-${gartnerIndustryCode}`}
          >
            <IndustryRow
              id={id}
              apIndustryNoMatchingOptions={
                apIndustriesOptions.length === 0 && !apIndustryCode
              }
              gartnerIndustryNoMatchingOptions={
                gartnerIndustriesOptions.length === 0 && !gartnerIndustryCode
              }
              computerEconomicsIndustryNoMatchingOptions={
                computerEconomicsIndustriesOptions.length === 0 &&
                !computerEconomicsIndustryCode
              }
              apIndustryCode={apIndustryCode}
              gartnerIndustryCode={gartnerIndustryCode}
              computerEconomicsIndustryCode={computerEconomicsIndustryCode}
              allocationPercentage={industry.allocationPercentage}
              apIndustriesOptions={
                apIndustriesOptions.length === 0
                  ? apIndustriesOptionsAll
                  : apIndustriesOptions
              }
              computerEconomicsIndustriesOptions={
                computerEconomicsIndustriesOptions.length === 0
                  ? computerEconomicsIndustriesOptionsAll
                  : computerEconomicsIndustriesOptions
              }
              gartnerIndustriesOptions={
                gartnerIndustriesOptions.length === 0
                  ? gartnerIndustriesOptionsAll
                  : gartnerIndustriesOptions
              }
              missingDataProviders={missingDataProviders}
              missingProviders={missingProviders}
              onChange={(id, obj) => {
                updateIndustries(id, {
                  ...industry,
                  ...obj,
                });
              }}
              isCorrectAllocation={isCorrectAllocation}
              onRemove={removeIndustry}
              onReset={resetIndustry}
            />
          </ConfigurationRow>
        );
      })}
      {!isCorrectAllocation ? (
        <ErrorRow>
          <ErrorText>{totalAllocation}% in total</ErrorText>
        </ErrorRow>
      ) : null}
      <ConfigurationRow>
        <ApButtonSecondary iconName="add" onClick={addIndustry}>
          Add Industry
        </ApButtonSecondary>
      </ConfigurationRow>
    </>
  );
};
